import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Icon } from '../icons';
import Combobox from './Combobox';
import Select from './Select';
import SearchInput from './SearchInput';
import Pagination, { usePagination, useFitPageSize } from './Pagination';
import SortableTh, { useSort, sortRows } from './SortableTh';
import HelpTip from './HelpTip';
import Tooltip from './Tooltip';
import DateTimePicker from './DateTimePicker';
import { RowMenu } from './RowMenu';
import KpiGrid from './KpiGrid';
import Drawer from './Drawer';
import Modal from './Modal';
import UsersPanel from './UsersPanel';
import { useConfirm } from './ConfirmProvider';
import { useAsyncAction } from '../hooks/useAsyncAction';

const api = (path, opts = {}) =>
  fetch(path, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...opts });

const fmt = (d) => (d ? new Date(d).toLocaleString('it-IT') : '—');
// Variante compatta per le celle-data degli elenchi: giorno + ora senza i
// secondi (es. "08/07/2026, 15:16"). In una tabella di riepilogo i secondi non
// sono informazione "a colpo d'occhio" e la stringa piena (~141px) non entra
// nelle colonne strette dei portatili, mandando la data a capo a meta' e
// disallineando le righe. Il timestamp completo resta nel title (hover) e nelle
// viste di dettaglio (modale rinnovo, storico), che continuano a usare fmt().
const fmtShort = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return `${dt.toLocaleDateString('it-IT')}, ${dt.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}`;
};

// Stati delle richieste di riattivazione: unico consumatore rimasto del badge
// generico, le altre entita' (licenze, codici) hanno etichetta IT dedicata.
const BADGE = { pending: 'badge-warn', approved: 'badge-success', rejected: 'badge-danger' };
const Badge = ({ v }) => <span className={`badge ${BADGE[v] || 'badge-muted'}`}>{v}</span>;

// Stati licenza con etichetta IT (il badge e' in maiuscolo: etichette corte) e
// classe cromatica. Copre il ciclo a due tempi: waiting -> ready -> active.
const LIC_STATUS = {
  waiting: { label: 'Setup', cls: 'badge-info' },
  ready_to_activate: { label: 'Pronta', cls: 'badge-warn' },
  active: { label: 'Attiva', cls: 'badge-success' },
  suspended: { label: 'Sospesa', cls: 'badge-warn' },
  revoked: { label: 'Revocata', cls: 'badge-danger' },
  cancelled: { label: 'Annullata', cls: 'badge-muted' },
};
// Stati "attivati" su cui l'amministratore puo' agire (riattiva/sospendi/revoca).
// Gli altri (waiting/ready/cancelled) sono guidati dal flusso tecnico.
const LIC_MANAGED = ['active', 'suspended', 'revoked'];

// Stati codice attivazione con etichetta IT, stessa terminologia del filtro
// "Tutti gli stati" della tabella qui sotto.
const CODE_STATUS = {
  unused: { label: 'Non usato', cls: 'badge-info' },
  used: { label: 'Usato', cls: 'badge-muted' },
  revoked: { label: 'Revocato', cls: 'badge-danger' },
  expired: { label: 'Scaduto', cls: 'badge-warn' },
};
const CodeBadge = ({ v }) => {
  const s = CODE_STATUS[v] || { label: v, cls: 'badge-muted' };
  return <span className={`badge ${s.cls}`}>{s.label}</span>;
};
const LicBadge = ({ v }) => {
  const s = LIC_STATUS[v] || { label: v, cls: 'badge-muted' };
  return <span className={`badge ${s.cls}`}>{s.label}</span>;
};

// Stati delle richieste di sblocco password admin: pending (in attesa che il
// tecnico generi un codice) -> approved (codice generato, in attesa che
// l'operatore lo usi) -> consumed (usato con successo) / expired (scaduto o
// bloccato dopo troppi tentativi errati) / rejected (invalidata da una nuova
// richiesta sullo stesso dispositivo).
const PW_RESET_STATUS = {
  pending: { label: 'In attesa', cls: 'badge-warn' },
  approved: { label: 'Codice generato', cls: 'badge-info' },
  consumed: { label: 'Consumato', cls: 'badge-success' },
  expired: { label: 'Scaduto', cls: 'badge-muted' },
  rejected: { label: 'Invalidata', cls: 'badge-muted' },
};
const PwResetBadge = ({ v }) => {
  const s = PW_RESET_STATUS[v] || { label: v, cls: 'badge-muted' };
  return <span className={`badge ${s.cls}`}>{s.label}</span>;
};

const Empty = ({ icon, text }) => (
  <div className="empty"><Icon name={icon} size={30} /><span>{text}</span></div>
);

const CODE_ACCESSORS = {
  code: { get: (c) => c.code, type: 'string' },
  area_group: { get: (c) => c.area_group, type: 'string' },
  area_label: { get: (c) => c.area_label, type: 'string' },
  status: { get: (c) => c.status, type: 'string' },
  created_at: { get: (c) => c.created_at, type: 'date' },
};
const DEVICE_ACCESSORS = {
  device_code: { get: (d) => d.device_code, type: 'string' },
  area_group: { get: (d) => d.area_group, type: 'string' },
  area_label: { get: (d) => d.area_label, type: 'string' },
  last_check_at: { get: (d) => d.last_check_at, type: 'date' },
  last_version: { get: (d) => d.last_version, type: 'string' },
  ip_address: { get: (d) => d.ip_address, type: 'string' },
};
const LICENSE_ACCESSORS = {
  device_code: { get: (l) => l.device_code, type: 'string' },
  activation_code: { get: (l) => l.activation_code, type: 'string' },
  status: { get: (l) => l.status, type: 'string' },
  issued_at: { get: (l) => l.issued_at, type: 'date' },
  last_check_at: { get: (l) => l.last_check_at, type: 'date' },
};
const REQUEST_ACCESSORS = {
  device_code: { get: (r) => r.device_code, type: 'string' },
  matched_components: { get: (r) => r.matched_components, type: 'number' },
  reason: { get: (r) => r.reason, type: 'string' },
  status: { get: (r) => r.status, type: 'string' },
  requested_at: { get: (r) => r.requested_at, type: 'date' },
};
const PW_RESET_ACCESSORS = {
  device_code: { get: (r) => r.device_code, type: 'string' },
  username: { get: (r) => r.username, type: 'string' },
  status: { get: (r) => r.status, type: 'string' },
  requested_at: { get: (r) => r.requested_at, type: 'date' },
  code_expires_at: { get: (r) => r.code_expires_at, type: 'date' },
};

// Tempo trascorso dall'ultimo contatto ("poco fa" / "12 min" / "3 h" / "5 gg").
//
// Volutamente NON mostriamo un badge online/offline: le macchine stanno dietro
// NAT e non sono raggiungibili, quindi l'unico segnale che abbiamo e' il momento
// in cui sono loro a chiamarci. Dichiarare "offline" una macchina accesa che
// semplicemente non ha ancora richiamato sarebbe un'informazione falsa: diciamo
// solo, onestamente, da quanto tempo non si fa sentire.
function sinceLabel(iso) {
  if (!iso) return 'Mai';
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'poco fa';
  if (min < 60) return `${min} min fa`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h fa`;
  return `${Math.floor(h / 24)} gg fa`;
}

// Dati del pannello amministratore e sezioni che li usano davvero.
//
// Serve a NON scaricare tutto a ogni cambio sezione: prima il pannello faceva
// le quattro chiamate anche solo per aprire "Utenti" (che non ne usa nessuna).
// Chi aggiunge una sezione deve dichiarare qui di cosa ha bisogno, altrimenti
// la sua tabella resta vuota.
const ADMIN_ENDPOINTS = {
  codes: '/api/admin/activation-codes',
  devices: '/api/admin/devices',
  licenses: '/api/admin/licenses',
  requests: '/api/admin/reactivation-requests',
  pwResets: '/api/admin/password-reset-requests',
};
const ALL_DATA_KEYS = Object.keys(ADMIN_ENDPOINTS);
const SECTION_DATA = {
  panoramica: ALL_DATA_KEYS,       // e' un riepilogo: li mostra tutti
  codici: ['codes', 'devices'],    // devices: alimenta i suggerimenti Gruppo/Etichetta
  dispositivi: ['devices'],
  licenze: ['licenses'],
  scadenziario: ['licenses'],
  riattivazioni: ['requests'],
  'admin-password-reset': ['pwResets'],
  utenti: [],                      // UsersPanel carica i propri dati da se'
};

// --- Scadenziario ---
// Soglia oltre la quale una licenza e' considerata "in scadenza" (giorni).
const EXPIRY_SOON_DAYS = 30;
// Un dispositivo e' "attivo di recente" se ha fatto check entro questi giorni.
const DEVICE_RECENT_DAYS = 7;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
// Giorni interi mancanti a una data ISO (negativo = gia' scaduta da |n| giorni).
function daysUntil(iso) {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / MS_PER_DAY);
}
const SCAD_ACCESSORS = {
  device_code: { get: (l) => l.device_code, type: 'string' },
  activation_code: { get: (l) => l.activation_code, type: 'string' },
  status: { get: (l) => l.status, type: 'string' },
  not_after: { get: (l) => l.not_after, type: 'date' },
};

// --- Rinnovo licenze ---
// Rinnovabili dal pannello solo le licenze attivate e utilizzabili.
const RENEWABLE = ['active', 'suspended'];
const RENEW_MAX_YEARS = 10;
const yearsLabel = (n) => `${n} ${Number(n) === 1 ? 'anno' : 'anni'}`;
// Somma anni mantenendo giorno/mese, con clamp del 29/02 (coerente col calcolo
// lato server via make_interval): l'anteprima combacia con la scadenza salvata.
function addYears(iso, years) {
  const d = new Date(iso);
  const day = d.getDate();
  d.setFullYear(d.getFullYear() + years);
  if (d.getDate() !== day) d.setDate(0);
  return d;
}
function addDays(iso, n) { const d = new Date(iso); d.setDate(d.getDate() + n); return d; }

// Stili della modale di rinnovo, iniettati una volta (stesso approccio di
// Modal/HelpTip). Usa solo variabili del design system gia' in uso altrove.
const RENEW_STYLE_ID = 'renew-modal-styles';
const RENEW_CSS = `
.renew-presets { display: flex; align-items: center; gap: .4rem; flex-wrap: wrap; }
.renew-years-input { width: 5.5rem; margin-left: .3rem; }
.renew-summary { margin: 1rem 0 .3rem; padding: .7rem .9rem; border: 1px solid var(--border-default); border-radius: var(--radius-sm); }
.renew-row { display: flex; justify-content: space-between; gap: 1rem; padding: .26rem 0; font-size: .9rem; }
.renew-row > span { color: var(--text-muted); }
.renew-row--hl { border-top: 1px dashed var(--border-default); margin-top: .3rem; padding-top: .5rem; font-size: 1rem; }
.renew-row--hl strong { color: var(--hiplaas-orange); }
.renew-note { display: flex; align-items: center; gap: .4rem; margin-top: .5rem; font-size: .78rem; color: var(--text-muted); }
.renew-check { display: flex; align-items: flex-start; gap: .5rem; margin-top: .9rem; font-size: .88rem; cursor: pointer; }
.renew-check .sel-check { margin-top: .1rem; }
.renew-history { margin-top: .9rem; font-size: .82rem; }
.renew-history summary { cursor: pointer; color: var(--text-muted); }
.renew-history ul { margin: .5rem 0 0; padding-left: 1.1rem; }
.renew-history li { margin-bottom: .25rem; }
.bulk-bar { display: flex; align-items: center; gap: .6rem; flex-wrap: wrap; margin-bottom: .7rem; padding: .5rem .75rem; border: 1px solid var(--hiplaas-orange); border-radius: var(--radius-sm); background: var(--glass-brand); }
.bulk-bar .bulk-count { font-weight: 600; font-size: .9rem; margin-right: auto; }
/* Checkbox disegnata: input nativo nascosto (opacity 0, sopra alla box) +
   span .sel-check-box che disegna la spunta. Stesso pattern del toggle-switch
   in Settings.js: coerente col resto del sistema invece della checkbox di
   default del browser. */
.sel-check { position: relative; display: inline-flex; width: 18px; height: 18px; flex-shrink: 0; cursor: pointer; vertical-align: middle; }
.sel-check input { position: absolute; inset: 0; margin: 0; opacity: 0; cursor: pointer; }
.sel-check-box { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; border-radius: 5px; border: 1.5px solid var(--border-default); background: var(--bg-base); color: transparent; pointer-events: none; transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease; }
.sel-check-box svg { width: 11px; height: 11px; transform: scale(0.6); opacity: 0; transition: transform 0.15s cubic-bezier(0.16,1,0.3,1), opacity 0.15s ease; }
.sel-check:hover .sel-check-box { border-color: var(--hiplaas-orange); }
.sel-check input:checked ~ .sel-check-box { background: var(--gradient-accent); border-color: transparent; color: #fff; }
.sel-check input:checked ~ .sel-check-box svg { transform: scale(1); opacity: 1; }
.sel-check input:focus-visible ~ .sel-check-box { outline: 2px solid var(--hiplaas-orange); outline-offset: 2px; }
.renew-bulk-list { margin: .9rem 0 .3rem; max-height: 230px; overflow-y: auto; border: 1px solid var(--border-default); border-radius: var(--radius-sm); }
.renew-bulk-row { display: flex; align-items: center; gap: .6rem; padding: .42rem .7rem; border-bottom: 1px solid var(--border-default); font-size: .84rem; }
.renew-bulk-row:last-child { border-bottom: none; }
.renew-bulk-row .rb-dev { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-weight: 600; min-width: 6.5rem; }
.renew-bulk-row .rb-dates { color: var(--text-muted); }
.renew-bulk-row .rb-arrow { opacity: .55; padding: 0 .15rem; }
.renew-bulk-row .rb-badge { margin-left: auto; }
.renew-fail-list { margin: .6rem 0 0; padding-left: 1.1rem; font-size: .82rem; color: var(--text-muted); }
.renew-fail-list li { margin-bottom: .2rem; }
/* Riquadro del codice di sblocco password, mostrato una sola volta al momento
   della generazione (stesso linguaggio visivo della password provvisoria in
   UsersPanel: box tratteggiato + pulsante copia). */
.pw-code-box { display: flex; align-items: center; gap: .75rem; margin-top: 1rem; padding: .7rem .85rem;
  border: 1px dashed var(--hiplaas-orange); border-radius: var(--radius-sm); background: var(--glass-brand); }
.pw-code-box .pw-code-main { flex: 1; min-width: 0; }
.pw-code-box .pw-code-label { font-size: .7rem; text-transform: uppercase; letter-spacing: .04em; color: var(--text-muted); margin-bottom: .15rem; }
.pw-code-box code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 1.15rem; font-weight: 700; letter-spacing: .08em; color: var(--text-primary); word-break: break-all; }
.pw-code-hint { font-size: .8rem; color: var(--text-muted); margin-top: .75rem; }
`;
function ensureRenewStyles() {
  if (typeof document === 'undefined' || document.getElementById(RENEW_STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = RENEW_STYLE_ID;
  s.textContent = RENEW_CSS;
  document.head.appendChild(s);
}

// Riquadro che mostra UNA sola volta il codice di sblocco appena generato, con
// pulsante "copia" (da leggere al telefono all'operatore). Il valore vive solo
// nello stato del dialog che lo mostra: chi lo apre e' responsabile di
// azzerarlo alla chiusura, cosi' non resta recuperabile in memoria.
function PwCodeBox({ code }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard non disponibile: il tecnico lo legge/copia a mano */ }
  };
  return (
    <div className="pw-code-box">
      <div className="pw-code-main">
        <div className="pw-code-label">Codice di sblocco</div>
        <code>{code}</code>
      </div>
      <button type="button" className="btn btn-ghost btn-sm" onClick={copy}>
        <Icon name={copied ? 'check' : 'copy'} size={14} />{copied ? 'Copiato' : 'Copia'}
      </button>
    </div>
  );
}

function DashboardAmministratore({ section, onBadges, user }) {
  useEffect(ensureRenewStyles, []);
  const confirm = useConfirm();
  const [codes, setCodes] = useState([]);
  const [devices, setDevices] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [requests, setRequests] = useState([]);
  const [pwResets, setPwResets] = useState([]);
  const [areaGroup, setAreaGroup] = useState('');
  const [areaLabel, setAreaLabel] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [message, setMessage] = useState(null);
  const [createCodeOpen, setCreateCodeOpen] = useState(false); // drawer "Crea codice"

  // Modifica gruppo/etichetta di un dispositivo (serve soprattutto a correggere,
  // una macchina alla volta, l'area segnaposto usata nella migrazione in blocco).
  const [editAreaTarget, setEditAreaTarget] = useState(null); // dispositivo in modifica
  const [editAreaGroup, setEditAreaGroup] = useState('');
  const [editAreaLabel, setEditAreaLabel] = useState('');
  const [editAreaSaving, setEditAreaSaving] = useState(false);
  const [editAreaError, setEditAreaError] = useState('');

  // Stesso concetto, per un codice attivazione non ancora usato (es. corregge
  // un errore di battitura prima che venga consegnato al tecnico).
  const [editCodeAreaTarget, setEditCodeAreaTarget] = useState(null); // codice in modifica
  const [editCodeAreaGroup, setEditCodeAreaGroup] = useState('');
  const [editCodeAreaLabel, setEditCodeAreaLabel] = useState('');
  const [editCodeAreaSaving, setEditCodeAreaSaving] = useState(false);
  const [editCodeAreaError, setEditCodeAreaError] = useState('');

  // Rinnovo licenza (modale condivisa da Licenze e Scadenziario).
  const [renewTarget, setRenewTarget] = useState(null);   // licenza da rinnovare
  const [renewYears, setRenewYears] = useState(1);         // durata in anni
  const [renewPaymentRef, setRenewPaymentRef] = useState('');
  const [renewConfirmed, setRenewConfirmed] = useState(false); // "pagamento verificato"
  const [renewing, setRenewing] = useState(false);
  const [renewError, setRenewError] = useState('');
  const [renewResult, setRenewResult] = useState(null);   // esito (renewal salvato)
  const [renewHistory, setRenewHistory] = useState([]);   // rinnovi precedenti

  // Selezione multipla per il rinnovo in blocco.
  const [selectedIds, setSelectedIds] = useState(() => new Set()); // id licenze selezionate
  const [renewBulkOpen, setRenewBulkOpen] = useState(false);       // modale rinnovo multiplo
  const [renewBulkResult, setRenewBulkResult] = useState(null);    // esito rinnovo multiplo

  // Modifica scadenza a data esatta (diversa dal rinnovo: qui si IMPOSTA un
  // valore, non si somma una durata). Serve per i casi correttivi — es. il
  // travaso dal vecchio server, dove la scadenza va allineata a una data
  // specifica già nota, non calcolata da adesso.
  const [editExpiryTarget, setEditExpiryTarget] = useState(null); // licenza in modifica
  const [editExpiryValue, setEditExpiryValue] = useState('');     // "AAAA-MM-GGTHH:mm" locale
  const [editExpiryError, setEditExpiryError] = useState('');

  // Ricerca/filtro per le tabelle (tutto client-side: i dati sono gia' in memoria).
  // L'ordinamento invece e' gestito cliccando le intestazioni di colonna (vedi useSort).
  const [codeSearch, setCodeSearch] = useState('');
  const [codeStatus, setCodeStatus] = useState('');
  // Stesso concetto del toggle "Mostra revocate" delle Licenze: acceso di
  // default, forzato acceso quando il filtro di stato è specificamente
  // 'revoked' (altrimenti la lista risulterebbe sempre vuota).
  const [showRevokedCode, setShowRevokedCode] = useState(true);
  const [deviceSearch, setDeviceSearch] = useState('');
  const [licSearch, setLicSearch] = useState('');
  const [licStatus, setLicStatus] = useState('');
  // Le revocate sono visibili di default (invariato); il toggle serve a
  // toglierle di torno quando la tabella si legge per "chi è ancora vivo".
  // Se lo stato filtrato è specificamente 'revoked' non ha senso spegnerlo
  // (la lista risulterebbe sempre vuota): resta forzato acceso, vedi effect
  // più sotto.
  const [showRevokedLic, setShowRevokedLic] = useState(true);
  const [reqSearch, setReqSearch] = useState('');
  const [reqStatus, setReqStatus] = useState('');
  const [pwSearch, setPwSearch] = useState('');
  const [pwStatus, setPwStatus] = useState('');
  const [scadSearch, setScadSearch] = useState('');
  const [scadFilter, setScadFilter] = useState('');
  const [codeColSort, onCodeSort] = useSort();
  const [deviceColSort, onDeviceSort] = useSort();
  const [licColSort, onLicSort] = useSort();
  const [reqColSort, onReqSort] = useSort();
  const [pwColSort, onPwSort] = useSort();
  const [scadColSort, onScadSort] = useSort();

  // Carica SOLO gli insiemi di dati richiesti. `keys` sono le chiavi di
  // ADMIN_ENDPOINTS / SECTION_DATA (vedi in cima al file).
  const loadData = useCallback(async (keys) => {
    if (!keys || !keys.length) return;
    try {
      const results = await Promise.all(
        keys.map((k) => api(ADMIN_ENDPOINTS[k]).then((x) => x.json()))
      );
      const data = {};
      keys.forEach((k, i) => { data[k] = results[i]; });

      if (data.codes) setCodes(data.codes);
      if (data.devices) setDevices(data.devices);
      if (data.licenses) setLicenses(data.licenses);
      if (data.requests) setRequests(data.requests);
      if (data.pwResets) setPwResets(data.pwResets);

      // I badge si aggiornano solo quando arrivano i dati che li alimentano.
      // Non e' l'unica fonte: lo stream SSE (App.js) li tiene comunque allineati
      // anche nelle sezioni che non caricano licenze/richieste — qui restano
      // corretti pure senza EventSource.
      const b = {};
      if (data.requests) {
        b.riattivazioni = data.requests.filter((x) => x.status === 'pending').length;
      }
      if (data.pwResets) {
        b.sblocchi = data.pwResets.filter((x) => x.status === 'pending').length;
      }
      if (data.licenses) {
        // Licenze non revocate gia' scadute o in scadenza entro la soglia
        // (i giorni negativi delle scadute rientrano in <=soglia).
        b.scadenziario = data.licenses.filter((x) => x.status !== 'revoked' && x.not_after != null && daysUntil(x.not_after) <= EXPIRY_SOON_DAYS).length;
      }
      if (Object.keys(b).length) onBadges(b);
    } catch { /* ignore */ }
  }, [onBadges]);

  // Ricarica TUTTO. Da usare dopo una MUTAZIONE, non per navigare: un'azione
  // amministrativa ha spesso effetti trasversali (approvare una riattivazione
  // tocca richieste, licenze e dispositivi insieme), quindi qui la ricarica
  // completa e' voluta — e costa una volta per azione, non a ogni click nel menu.
  const loadAll = useCallback(() => loadData(ALL_DATA_KEYS), [loadData]);

  // Navigazione: ogni sezione carica solo cio' che le serve, a ogni ingresso.
  // Dati sempre freschi senza scaricare l'intero pannello (aprire "Utenti" non
  // deve costare quattro interrogazioni al database).
  useEffect(() => { loadData(SECTION_DATA[section] || []); }, [section, loadData]);

  // La selezione multipla non deve sopravvivere al cambio sezione: prima ci
  // pensava il rimontaggio della vista, ora che resta montata va azzerata a mano
  // (altrimenti in Scadenziario ti ritrovi selezionate righe scelte in Licenze).
  // Stesso motivo per i messaggi di esito, che si riferiscono all'azione appena
  // fatta e non hanno senso in un'altra sezione.
  useEffect(() => {
    setSelectedIds(new Set());
    setMessage(null);
  }, [section]);

  // Presenza dei dispositivi in tempo reale. Lo stream si apre SOLO mentre la
  // sezione e' a video: e' l'unica che mostra questi dati, e tenere aperta una
  // connessione per una tabella che nessuno sta guardando sarebbe uno spreco.
  // Aggiorna la riga interessata IN PLACE: niente ricarica dell'intero elenco a
  // ogni check-in (con molti dispositivi sarebbe una richiesta continua).
  useEffect(() => {
    if (section !== 'dispositivi') return;
    if (typeof EventSource === 'undefined') return; // resta la foto iniziale

    const es = new EventSource('/api/admin/devices/stream', { withCredentials: true });
    es.onmessage = (e) => {
      let msg;
      try { msg = JSON.parse(e.data); } catch { return; }
      if (msg.type !== 'device_seen' || !msg.device) return;
      setDevices((prev) => prev.map((d) => (d.id === msg.device.id ? { ...d, ...msg.device } : d)));
    };
    // In caso di errore EventSource riconnette da solo; la foto a video resta
    // valida (i dati durevoli sono a DB), al massimo si perde qualche battito.

    return () => es.close();
  }, [section]);

  // Le etichette "3 min fa" devono invecchiare anche quando non arriva nulla:
  // senza questo tick resterebbero congelate all'ultimo render.
  const [, setClockTick] = useState(0);
  useEffect(() => {
    if (section !== 'dispositivi') return;
    const t = setInterval(() => setClockTick((n) => n + 1), 30000);
    return () => clearInterval(t);
  }, [section]);

  // Valori distinti gia' in uso, per popolare i menu a tendina di Gruppo/Etichetta.
  const groupOptions = useMemo(
    () => [...codes, ...devices].map((x) => x.area_group).filter(Boolean),
    [codes, devices]
  );
  const labelOptions = useMemo(
    () => [...codes, ...devices].map((x) => x.area_label).filter(Boolean),
    [codes, devices]
  );

  const matches = (q, values) => !q || values.some((v) => (v || '').toLowerCase().includes(q));

  const filteredCodes = useMemo(() => {
    const q = codeSearch.trim().toLowerCase();
    return codes.filter((c) => (!codeStatus || c.status === codeStatus)
      && (showRevokedCode || c.status !== 'revoked')
      && matches(q, [c.code, c.area_group, c.area_label]));
  }, [codes, codeSearch, codeStatus, showRevokedCode]);

  useEffect(() => {
    if (codeStatus === 'revoked' && !showRevokedCode) setShowRevokedCode(true);
  }, [codeStatus, showRevokedCode]);

  const filteredDevices = useMemo(() => {
    const q = deviceSearch.trim().toLowerCase();
    return devices.filter((d) => matches(q, [d.device_code, d.area_group, d.area_label]));
  }, [devices, deviceSearch]);

  const filteredLicenses = useMemo(() => {
    const q = licSearch.trim().toLowerCase();
    return licenses.filter((l) => (!licStatus || l.status === licStatus)
      && (showRevokedLic || l.status !== 'revoked')
      && matches(q, [l.device_code, l.activation_code]));
  }, [licenses, licSearch, licStatus, showRevokedLic]);

  // Selezionare "Revocata" dal filtro di stato (dropdown o KPI) non ha senso
  // insieme al toggle spento: riaccenderlo qui evita una tabella sempre vuota.
  useEffect(() => {
    if (licStatus === 'revoked' && !showRevokedLic) setShowRevokedLic(true);
  }, [licStatus, showRevokedLic]);

  const filteredRequests = useMemo(() => {
    const q = reqSearch.trim().toLowerCase();
    return requests.filter((r) => (!reqStatus || r.status === reqStatus) && matches(q, [r.device_code, r.reason]));
  }, [requests, reqSearch, reqStatus]);

  const filteredPwResets = useMemo(() => {
    const q = pwSearch.trim().toLowerCase();
    return pwResets.filter((r) => (!pwStatus || r.status === pwStatus) && matches(q, [r.device_code, r.username]));
  }, [pwResets, pwSearch, pwStatus]);

  const sortedCodes = useMemo(() => sortRows(filteredCodes, codeColSort, CODE_ACCESSORS), [filteredCodes, codeColSort]);
  const sortedDevices = useMemo(() => sortRows(filteredDevices, deviceColSort, DEVICE_ACCESSORS), [filteredDevices, deviceColSort]);
  const sortedLicenses = useMemo(() => sortRows(filteredLicenses, licColSort, LICENSE_ACCESSORS), [filteredLicenses, licColSort]);
  const sortedRequests = useMemo(() => sortRows(filteredRequests, reqColSort, REQUEST_ACCESSORS), [filteredRequests, reqColSort]);
  const sortedPwResets = useMemo(() => sortRows(filteredPwResets, pwColSort, PW_RESET_ACCESSORS), [filteredPwResets, pwColSort]);

  // Scadenziario: licenze non revocate con una scadenza (not_after), pre-ordinate
  // per scadenza piu' vicina; e' l'ordine di default finche' non si ordina per colonna.
  const expiringBase = useMemo(
    () => licenses
      .filter((l) => l.status !== 'revoked' && l.not_after != null)
      .slice()
      .sort((a, b) => new Date(a.not_after) - new Date(b.not_after)),
    [licenses]
  );
  const filteredScad = useMemo(() => {
    const q = scadSearch.trim().toLowerCase();
    return expiringBase.filter((l) => {
      if (!matches(q, [l.device_code, l.activation_code])) return false;
      if (!scadFilter) return true;
      const d = daysUntil(l.not_after);
      if (scadFilter === 'expired') return d < 0;
      if (scadFilter === 'soon') return d >= 0 && d <= EXPIRY_SOON_DAYS;
      if (scadFilter === 'valid') return d > EXPIRY_SOON_DAYS;
      return true;
    });
  }, [expiringBase, scadSearch, scadFilter]);
  const sortedScad = useMemo(() => sortRows(filteredScad, scadColSort, SCAD_ACCESSORS), [filteredScad, scadColSort]);

  // KPI dello Scadenziario: sempre calcolate sull'intero insieme monitorato
  // (non sul filtro/ricerca), cosi' restano una fotografia d'insieme.
  const scadStats = useMemo(() => {
    let expired = 0, soon = 0, valid = 0, nextDays = null;
    for (const l of expiringBase) {
      const d = daysUntil(l.not_after);
      if (d < 0) expired += 1;
      else if (d <= EXPIRY_SOON_DAYS) soon += 1;
      else valid += 1;
      if (d >= 0 && (nextDays === null || d < nextDays)) nextDays = d;
    }
    return { expired, soon, valid, total: expiringBase.length, nextDays };
  }, [expiringBase]);

  // KPI di Licenze / Dispositivi / Riattivazioni (sull'intero insieme, non sul filtro).
  const licStats = useMemo(() => ({
    total: licenses.length,
    in_activation: licenses.filter((l) => l.status === 'waiting' || l.status === 'ready_to_activate').length,
    active: licenses.filter((l) => l.status === 'active').length,
    suspended: licenses.filter((l) => l.status === 'suspended').length,
    revoked: licenses.filter((l) => l.status === 'revoked').length,
  }), [licenses]);
  const deviceStats = useMemo(() => {
    let recent = 0;
    const versions = new Set();
    for (const d of devices) {
      if (d.last_check_at && daysUntil(d.last_check_at) >= -DEVICE_RECENT_DAYS) recent += 1;
      if (d.last_version) versions.add(d.last_version);
    }
    return { total: devices.length, recent, silent: devices.length - recent, versions: versions.size };
  }, [devices]);
  const reqStats = useMemo(() => ({
    total: requests.length,
    pending: requests.filter((r) => r.status === 'pending').length,
    approved: requests.filter((r) => r.status === 'approved').length,
    rejected: requests.filter((r) => r.status === 'rejected').length,
  }), [requests]);
  const pwResetStats = useMemo(() => ({
    total: pwResets.length,
    pending: pwResets.filter((r) => r.status === 'pending').length,
    approved: pwResets.filter((r) => r.status === 'approved').length,
    consumed: pwResets.filter((r) => r.status === 'consumed').length,
  }), [pwResets]);

  // pageSize iniziale piccola (6): il fit-to-screen la ricalcola subito, cosi'
  // si evita un lampo di righe in eccesso al primo render.
  const codePagination = usePagination(sortedCodes.length, [codeSearch, codeStatus, codeColSort], 6);
  const devicePagination = usePagination(sortedDevices.length, [deviceSearch, deviceColSort], 6);
  const licPagination = usePagination(sortedLicenses.length, [licSearch, licStatus, licColSort], 6);
  const reqPagination = usePagination(sortedRequests.length, [reqSearch, reqStatus, reqColSort], 6);
  const pwResetPagination = usePagination(sortedPwResets.length, [pwSearch, pwStatus, pwColSort], 6);
  const scadPagination = usePagination(sortedScad.length, [scadSearch, scadFilter, scadColSort], 6);

  // Nessuna tabella-elenco deve far scrollare la pagina: altezza della card e
  // righe per pagina si adattano allo spazio visibile (pattern di riferimento
  // definito sulle Licenze, esteso a tutte le liste principali).
  const codeCardRef = useRef(null);
  const codeWrapRef = useRef(null);
  const codeTheadRef = useRef(null);
  const deviceCardRef = useRef(null);
  const deviceWrapRef = useRef(null);
  const deviceTheadRef = useRef(null);
  const licCardRef = useRef(null);
  const licWrapRef = useRef(null);
  const licTheadRef = useRef(null);
  const reqCardRef = useRef(null);
  const reqWrapRef = useRef(null);
  const reqTheadRef = useRef(null);
  const pwCardRef = useRef(null);
  const pwWrapRef = useRef(null);
  const pwTheadRef = useRef(null);
  const scadCardRef = useRef(null);
  const scadWrapRef = useRef(null);
  const scadTheadRef = useRef(null);
  useFitPageSize(
    { cardRef: codeCardRef, wrapRef: codeWrapRef, theadRef: codeTheadRef, bottomGap: 32 },
    codePagination.pageSize, codePagination.setPageSize,
    section === 'codici' && codes.length > 0
  );
  useFitPageSize(
    { cardRef: deviceCardRef, wrapRef: deviceWrapRef, theadRef: deviceTheadRef, bottomGap: 32 },
    devicePagination.pageSize, devicePagination.setPageSize,
    section === 'dispositivi' && devices.length > 0
  );
  useFitPageSize(
    { cardRef: reqCardRef, wrapRef: reqWrapRef, theadRef: reqTheadRef, bottomGap: 32 },
    reqPagination.pageSize, reqPagination.setPageSize,
    section === 'riattivazioni' && requests.length > 0
  );
  useFitPageSize(
    { cardRef: pwCardRef, wrapRef: pwWrapRef, theadRef: pwTheadRef, bottomGap: 32 },
    pwResetPagination.pageSize, pwResetPagination.setPageSize,
    section === 'admin-password-reset' && pwResets.length > 0
  );
  useFitPageSize(
    { cardRef: scadCardRef, wrapRef: scadWrapRef, theadRef: scadTheadRef, bottomGap: 32 },
    scadPagination.pageSize, scadPagination.setPageSize,
    section === 'scadenziario' && licenses.length > 0
  );

  const pagedCodes = sortedCodes.slice(codePagination.start, codePagination.end);
  const pagedDevices = sortedDevices.slice(devicePagination.start, devicePagination.end);
  const pagedLicenses = sortedLicenses.slice(licPagination.start, licPagination.end);
  const pagedScad = sortedScad.slice(scadPagination.start, scadPagination.end);
  const pagedRequests = sortedRequests.slice(reqPagination.start, reqPagination.end);
  const pagedPwResets = sortedPwResets.slice(pwResetPagination.start, pwResetPagination.end);

  const createCode = async (e) => {
    e.preventDefault();
    setMessage(null);
    const body = { area_group: areaGroup.trim(), area_label: areaLabel.trim() };
    if (expiresAt) body.expires_at = new Date(expiresAt).toISOString();
    const res = await api('/api/admin/activation-codes', { method: 'POST', body: JSON.stringify(body) });
    const data = await res.json();
    if (!res.ok) { setMessage({ type: 'error', text: data.error || 'Errore' }); return; }
    setMessage({ type: 'success', text: `Codice creato: ${data.code}` });
    setAreaGroup(''); setAreaLabel(''); setExpiresAt('');
    loadAll();
  };
  const { run: runCreateCode, pending: creatingCode } = useAsyncAction(createCode);

  const revokeCode = async (id) => {
    if (!(await confirm({
      title: 'Revoca codice',
      message: 'Revocare questo codice attivazione?\n\nNon potrà più essere usato per attivare una macchina. Azione non reversibile.',
      confirmLabel: 'Revoca codice',
      tone: 'danger',
      icon: 'ban',
    }))) return;
    await api(`/api/admin/activation-codes/${id}`, { method: 'PATCH', body: JSON.stringify({ status: 'revoked' }) });
    loadAll();
  };
  const { run: runRevokeCode, pending: revokingCode } = useAsyncAction(revokeCode);

  // Eliminazione fisica (superadmin/sviluppatore, vedi canDeleteRecords più
  // sotto): a differenza delle azioni "morbide" di questa pagina, qui non c'è
  // modo di tornare indietro — la riga sparisce dal database, non solo dallo
  // stato utilizzabile.
  const deleteDevice = async (device) => {
    if (!(await confirm({
      title: 'Elimina dispositivo',
      message: `Eliminare definitivamente ${device.device_code} e tutte le sue licenze?\n\nAzione irreversibile: il manager dimentica questo hardware, alla prossima registrazione ne creerebbe uno nuovo.`,
      confirmLabel: 'Elimina definitivamente',
      tone: 'danger',
      icon: 'trash',
    }))) return;
    const res = await api(`/api/admin/devices/${device.id}`, { method: 'DELETE' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      confirm({ title: 'Eliminazione non consentita', message: data.error || 'Impossibile eliminare il dispositivo.', confirmLabel: 'Ho capito', hideCancel: true, icon: 'alert' });
      return;
    }
    loadAll();
  };
  const { run: runDeleteDevice, pending: deletingDevice } = useAsyncAction(deleteDevice);

  const deleteActivationCode = async (code) => {
    // Un codice 'used' e' gia' stato consumato da una licenza: cancellarlo non
    // tocca ne' la licenza ne' il dispositivo (FK ON DELETE SET NULL), ma
    // perdono il riferimento a con quale codice furono attivati — avviso extra.
    const usedWarning = code.status === 'used'
      ? '\n\nQuesto codice è già stato usato: il dispositivo e la licenza che lo hanno consumato restano intatti, ma perderanno il riferimento a quale codice li ha attivati.'
      : '';
    if (!(await confirm({
      title: 'Elimina codice',
      message: `Eliminare definitivamente il codice ${code.code}?\n\nA differenza della revoca, la riga sparisce dal database. Azione irreversibile.${usedWarning}`,
      confirmLabel: 'Elimina definitivamente',
      tone: 'danger',
      icon: 'trash',
    }))) return;
    const res = await api(`/api/admin/activation-codes/${code.id}`, { method: 'DELETE' });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      confirm({ title: 'Eliminazione non consentita', message: data.error || 'Impossibile eliminare il codice.', confirmLabel: 'Ho capito', hideCancel: true, icon: 'alert' });
      return;
    }
    loadAll();
  };
  const { run: runDeleteCode, pending: deletingCode } = useAsyncAction(deleteActivationCode);

  // Dispositivi/Codici: eliminazione fisica riservata a superadmin/sviluppatore
  // (requireRole aggiuntivo lato server su questi endpoint specifici — qui è
  // solo esperienza d'uso, la sicurezza vera è server-side).
  const canDeleteRecords = user?.role === 'superadmin' || user?.role === 'sviluppatore';

  // Conferma richiesta prima delle azioni che bloccano una macchina (le due
  // "leve"): la riattivazione non ha conferma perche' ripristina soltanto.
  const setLicenseStatus = async (id, status) => {
    if (status === 'suspended' && !(await confirm({
      title: 'Blocca aggiornamenti',
      message: 'Bloccare gli AGGIORNAMENTI di questa licenza?\n\nL\'app continua a funzionare, ma la macchina non riceverà più aggiornamenti. Azione reversibile (es. pagamento in ritardo).',
      confirmLabel: 'Blocca aggiornamenti',
      tone: 'warn',
      icon: 'pause',
    }))) return;
    if (status === 'revoked' && !(await confirm({
      title: 'Blocca accesso',
      message: 'Bloccare l\'ACCESSO di questa licenza?\n\nL\'app non si aprirà più sulla macchina: immediato se online, entro la scadenza della licenza se offline.',
      confirmLabel: 'Blocca accesso',
      tone: 'danger',
      icon: 'ban',
    }))) return;
    await api(`/api/admin/licenses/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) });
    loadAll();
  };
  const { run: runSetLicenseStatus, pending: settingLicenseStatus } = useAsyncAction(setLicenseStatus);
  // Riporta una licenza attiva in configurazione, allo stato iniziale del tecnico
  // ('waiting'): azzera la decorrenza e la macchina torna in setup. Il tecnico
  // dovra' ripetere la configurazione e dichiararla completata; NON si riattiva
  // da sola. La postazione se ne accorge al battito successivo.
  const revertLicense = async (id) => {
    if (!(await confirm({
      title: 'Riporta in configurazione',
      message: 'Riportare questa postazione in configurazione?\n\nLa decorrenza della licenza viene azzerata e la macchina torna in configurazione, come dopo l\'inserimento del codice. Il tecnico dovrà completare di nuovo la configurazione e riavviare per riattivarla.',
      confirmLabel: 'Riporta in configurazione',
      tone: 'warn',
      icon: 'sliders',
    }))) return;
    await api(`/api/admin/licenses/${id}/revert`, { method: 'POST' });
    loadAll();
  };
  const { run: runRevertLicense, pending: revertingLicense } = useAsyncAction(revertLicense);
  const resolveRequest = async (id, status) => {
    await api(`/api/admin/reactivation-requests/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) });
    loadAll();
  };
  const { run: runResolveRequest, pending: resolvingRequest } = useAsyncAction(resolveRequest);

  // --- Sblocco password admin ---
  // Il codice generato vive SOLO qui, nello stato locale del dialog che lo
  // mostra: niente store globale, niente persistenza. Chiudere il dialog
  // (onClose sotto, nel JSX) azzera `pwCodeModal` e con esso il codice stesso,
  // che a quel punto non e' piu' recuperabile da nessuna parte nel pannello.
  const [pwCodeModal, setPwCodeModal] = useState(null); // { request, code, expiresAt }
  const [pwNotice, setPwNotice] = useState(null); // esito/errore dell'ultima generazione
  const generatePwResetCode = async (r) => {
    setPwNotice(null);
    try {
      const res = await api(`/api/admin/password-reset-requests/${r.id}/generate-code`, { method: 'POST' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const text = res.status === 404
          ? 'Richiesta non trovata: potrebbe essere stata rimossa nel frattempo. Aggiorna l\'elenco.'
          : res.status === 409
          ? 'Non e\' piu\' possibile generare un codice per questa richiesta: e\' gia\' stato generato oppure la richiesta non e\' piu\' in attesa (scaduta o gestita da un altro tecnico).'
          : (data.error || 'Errore durante la generazione del codice.');
        setPwNotice({ type: 'error', text });
        loadData(['pwResets']); // riallinea lo stato mostrato con quello reale
        return;
      }
      setPwCodeModal({ request: r, code: data.code, expiresAt: data.expires_at });
      loadData(['pwResets']);
    } catch {
      setPwNotice({ type: 'error', text: 'Impossibile contattare il server.' });
    }
  };
  const { run: runGeneratePwCode, pending: generatingPwCode } = useAsyncAction(generatePwResetCode);
  const closePwCodeModal = () => setPwCodeModal(null);

  // --- Selezione multipla ---
  const toggleSelect = (id) => setSelectedIds((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const clearSelection = () => setSelectedIds(new Set());
  // Seleziona/deseleziona tutte le righe rinnovabili dell'elenco corrente.
  const selectAllRenewable = (rows) => setSelectedIds((prev) => {
    const n = new Set(prev);
    const renewable = rows.filter((l) => RENEWABLE.includes(l.status));
    const allSel = renewable.length > 0 && renewable.every((l) => n.has(l.id));
    renewable.forEach((l) => { if (allSel) n.delete(l.id); else n.add(l.id); });
    return n;
  });

  const openEditArea = (device) => {
    setEditAreaTarget(device);
    setEditAreaGroup(device.area_group || '');
    setEditAreaLabel(device.area_label || '');
    setEditAreaError('');
  };
  const closeEditArea = () => {
    setEditAreaTarget(null);
    setEditAreaError('');
  };
  const submitEditArea = async (e) => {
    e.preventDefault();
    const group = editAreaGroup.trim();
    const label = editAreaLabel.trim();
    if (!group || !label) { setEditAreaError('Gruppo ed etichetta sono entrambi richiesti'); return; }
    setEditAreaSaving(true);
    setEditAreaError('');
    try {
      const res = await api(`/api/admin/devices/${editAreaTarget.id}`, {
        method: 'PATCH', body: JSON.stringify({ area_group: group, area_label: label }),
      });
      const data = await res.json();
      if (!res.ok) { setEditAreaError(data.error || 'Errore durante il salvataggio'); return; }
      closeEditArea();
      loadAll();
    } catch {
      setEditAreaError('Impossibile contattare il server');
    } finally {
      setEditAreaSaving(false);
    }
  };

  const openEditCodeArea = (code) => {
    setEditCodeAreaTarget(code);
    setEditCodeAreaGroup(code.area_group || '');
    setEditCodeAreaLabel(code.area_label || '');
    setEditCodeAreaError('');
  };
  const closeEditCodeArea = () => {
    setEditCodeAreaTarget(null);
    setEditCodeAreaError('');
  };
  const submitEditCodeArea = async (e) => {
    e.preventDefault();
    const group = editCodeAreaGroup.trim();
    const label = editCodeAreaLabel.trim();
    if (!group || !label) { setEditCodeAreaError('Gruppo ed etichetta sono entrambi richiesti'); return; }
    setEditCodeAreaSaving(true);
    setEditCodeAreaError('');
    try {
      const res = await api(`/api/admin/activation-codes/${editCodeAreaTarget.id}`, {
        method: 'PATCH', body: JSON.stringify({ area_group: group, area_label: label }),
      });
      const data = await res.json();
      if (!res.ok) { setEditCodeAreaError(data.error || 'Errore durante il salvataggio'); return; }
      closeEditCodeArea();
      loadAll();
    } catch {
      setEditCodeAreaError('Impossibile contattare il server');
    } finally {
      setEditCodeAreaSaving(false);
    }
  };

  const closeRenew = () => {
    setRenewTarget(null);
    setRenewBulkOpen(false);
    setRenewResult(null);
    setRenewBulkResult(null);
  };

  // Converte un timestamp ISO (dal server, UTC) nel formato locale
  // "AAAA-MM-GGTHH:mm" atteso da DateTimePicker — con i getter locali di
  // Date, non quelli UTC, cosi' l'utente vede l'ora del proprio fuso e il
  // roundtrip con new Date(valore).toISOString() torna coerente.
  const toLocalDateTimeValue = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const openEditExpiry = (lic) => {
    setEditExpiryTarget(lic);
    setEditExpiryValue(toLocalDateTimeValue(lic.not_after));
    setEditExpiryError('');
  };
  const closeEditExpiry = () => {
    setEditExpiryTarget(null);
    setEditExpiryValue('');
    setEditExpiryError('');
  };
  const saveEditExpiry = async () => {
    if (!editExpiryValue) { setEditExpiryError('Seleziona una data'); return; }
    setEditExpiryError('');
    try {
      const res = await api(`/api/admin/licenses/${editExpiryTarget.id}/expiry`, {
        method: 'PATCH', body: JSON.stringify({ not_after: new Date(editExpiryValue).toISOString() }),
      });
      const data = await res.json();
      if (!res.ok) { setEditExpiryError(data.error || 'Errore durante il salvataggio'); return; }
      closeEditExpiry();
      loadAll();
    } catch {
      setEditExpiryError('Impossibile contattare il server');
    }
  };
  const { run: runSaveEditExpiry, pending: savingEditExpiry } = useAsyncAction(saveEditExpiry);

  // Apre la modale di rinnovo su una licenza e ne carica lo storico rinnovi.
  const openRenew = async (lic) => {
    setRenewTarget(lic);
    setRenewBulkOpen(false);
    setRenewBulkResult(null);
    setRenewYears(1);
    setRenewPaymentRef('');
    setRenewConfirmed(false);
    setRenewError('');
    setRenewResult(null);
    setRenewHistory([]);
    try {
      const h = await api(`/api/admin/licenses/${lic.id}/renewals`).then((x) => x.json());
      if (Array.isArray(h)) setRenewHistory(h);
    } catch { /* storico non bloccante */ }
  };

  // Apre la modale di rinnovo multiplo sulle licenze selezionate.
  const openBulkRenew = () => {
    setRenewTarget(null);
    setRenewBulkOpen(true);
    setRenewBulkResult(null);
    setRenewYears(1);
    setRenewPaymentRef('');
    setRenewConfirmed(false);
    setRenewError('');
    setRenewResult(null);
  };

  const submitRenewBulk = async (e) => {
    e.preventDefault();
    setRenewError('');
    const y = Number(renewYears);
    if (!Number.isInteger(y) || y < 1) { setRenewError('La durata deve essere di almeno 1 anno'); return; }
    if (y > RENEW_MAX_YEARS) { setRenewError(`La durata massima è ${RENEW_MAX_YEARS} anni`); return; }
    if (!renewConfirmed) { setRenewError('Conferma di aver verificato il pagamento delle licenze selezionate'); return; }
    const ids = licenses.filter((l) => selectedIds.has(l.id) && RENEWABLE.includes(l.status)).map((l) => l.id);
    if (ids.length === 0) { setRenewError('Nessuna licenza rinnovabile selezionata'); return; }
    setRenewing(true);
    try {
      const body = { license_ids: ids, years: y, confirm: true };
      if (renewPaymentRef.trim()) body.payment_reference = renewPaymentRef.trim();
      const res = await api('/api/admin/licenses/renew-bulk', { method: 'POST', body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) { setRenewError(data.error || 'Errore durante il rinnovo'); return; }
      setRenewBulkResult(data);
      clearSelection();
      loadAll();
    } catch {
      setRenewError('Impossibile contattare il server');
    } finally {
      setRenewing(false);
    }
  };

  const submitRenew = async (e) => {
    e.preventDefault();
    setRenewError('');
    const y = Number(renewYears);
    if (!Number.isInteger(y) || y < 1) { setRenewError('La durata deve essere di almeno 1 anno'); return; }
    if (y > RENEW_MAX_YEARS) { setRenewError(`La durata massima è ${RENEW_MAX_YEARS} anni`); return; }
    if (!renewConfirmed) { setRenewError('Conferma di aver verificato il pagamento del cliente'); return; }
    setRenewing(true);
    try {
      const body = { years: y, confirm: true };
      if (renewPaymentRef.trim()) body.payment_reference = renewPaymentRef.trim();
      const res = await api(`/api/admin/licenses/${renewTarget.id}/renew`, { method: 'POST', body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) { setRenewError(data.error || 'Errore durante il rinnovo'); return; }
      setRenewResult(data.renewal);
      loadAll();
    } catch {
      setRenewError('Impossibile contattare il server');
    } finally {
      setRenewing(false);
    }
  };

  // Elemento modale calcolato una volta e incluso sia in Licenze sia in Scadenziario.
  // Licenze selezionate e rinnovabili (per barra bulk e modale multipla).
  const selectedRenewable = licenses.filter((l) => selectedIds.has(l.id) && RENEWABLE.includes(l.status));
  const selectedCount = selectedRenewable.length;
  // Barra azioni condivisa da Licenze e Scadenziario quando c'è una selezione.
  const bulkBar = selectedCount > 0 ? (
    <div className="bulk-bar">
      <span className="bulk-count">{selectedCount} {selectedCount === 1 ? 'licenza selezionata' : 'licenze selezionate'}</span>
      <button type="button" className="btn btn-accent btn-sm" onClick={openBulkRenew}><Icon name="refresh" size={14} />Rinnova selezionate</button>
      <button type="button" className="btn btn-ghost btn-sm" onClick={clearSelection}>Deseleziona</button>
    </div>
  ) : null;

  // Modale di rinnovo: gestisce sia il singolo (renewTarget) sia il multiplo
  // (renewBulkOpen). Nel multiplo ogni licenza calcola la propria decorrenza.
  const renewOpen = !!renewTarget || renewBulkOpen;
  const renewList = renewTarget ? [renewTarget] : (renewBulkOpen ? selectedRenewable : []);
  const renewModal = renewOpen && (() => {
    const isBulk = !renewTarget;
    const y = Number(renewYears);
    const validY = Number.isInteger(y) && y >= 1 && y <= RENEW_MAX_YEARS;
    const now = Date.now();
    const previews = renewList.map((l) => {
      const cur = l.not_after || null;
      const wasExpired = cur ? new Date(cur).getTime() <= now : true;
      const base = wasExpired ? new Date() : new Date(cur);
      const newStart = wasExpired ? new Date() : addDays(cur, 1);
      const newNotAfter = validY ? addYears(base.toISOString(), y) : null;
      return { l, cur, wasExpired, newStart, newNotAfter };
    });
    const expiredCount = previews.filter((p) => p.wasExpired).length;
    const single = previews[0];
    const done = renewResult || renewBulkResult;
    return (
      <Modal title={isBulk ? `Rinnova ${renewList.length} licenze` : `Rinnova licenza — ${renewTarget.device_code || 's/n'}`} icon="refresh" onClose={closeRenew}>
        {done ? (
          <div className="modal-body">
            {renewBulkResult ? (
              <>
                <div className={`msg ${renewBulkResult.failed ? 'msg-warn' : 'msg-success'}`}><Icon name={renewBulkResult.failed ? 'alert' : 'check'} size={16} />{renewBulkResult.renewed} {renewBulkResult.renewed === 1 ? 'licenza rinnovata' : 'licenze rinnovate'}{renewBulkResult.failed ? `, ${renewBulkResult.failed} non riuscite` : ''}.</div>
                {renewBulkResult.failed > 0 && (
                  <ul className="renew-fail-list">
                    {renewBulkResult.results.filter((r) => !r.ok).map((r) => (
                      <li key={r.id}>Licenza #{r.id}: {r.error}</li>
                    ))}
                  </ul>
                )}
              </>
            ) : (
              <>
                <div className="msg msg-success"><Icon name="check" size={16} />Rinnovo registrato. Nuova scadenza: <strong>{fmt(renewResult.new_not_after)}</strong>.</div>
                <p className="renew-note"><Icon name="check" size={13} />Salvato nello storico della licenza; la licenza è stata ri-firmata con la nuova scadenza.</p>
              </>
            )}
            <div className="modal-actions">
              <button type="button" className="btn btn-primary" onClick={closeRenew}>Chiudi</button>
            </div>
          </div>
        ) : (
          <form className="modal-body" onSubmit={isBulk ? submitRenewBulk : submitRenew}>
            {isBulk
              ? <div className={`msg ${expiredCount ? 'msg-warn' : 'msg-info'}`} style={{ marginBottom: '.9rem' }}><Icon name={expiredCount ? 'alert' : 'shield'} size={16} />{renewList.length} licenze selezionate{expiredCount ? ` · ${expiredCount} già scadute (decorrono da adesso)` : ''}. Stessa durata e riferimento per tutte.</div>
              : (single.wasExpired && <div className="msg msg-warn" style={{ marginBottom: '.9rem' }}><Icon name="alert" size={16} />Questa licenza risulta già scaduta{single.cur ? ` il ${fmt(single.cur)}` : ''}. Il rinnovo decorrerà da adesso.</div>)}
            {renewError && <div className="msg msg-error" style={{ marginBottom: '.9rem' }}><Icon name="alert" size={16} />{renewError}</div>}

            <div className="field">
              <label htmlFor="rn-years">Durata del rinnovo (anni)</label>
              <div className="renew-presets">
                {[1, 2, 3].map((n) => (
                  <button type="button" key={n} className={`btn btn-sm ${y === n ? 'btn-accent' : 'btn-ghost'}`} onClick={() => setRenewYears(n)}>{n}</button>
                ))}
                <input id="rn-years" className="input renew-years-input" type="number" min="1" max={RENEW_MAX_YEARS} step="1"
                  value={renewYears} onChange={(e) => setRenewYears(e.target.value === '' ? '' : Number(e.target.value))} aria-label="Numero di anni" />
              </div>
            </div>

            <div className="field">
              <label htmlFor="rn-pay">Riferimento pagamento (opzionale)</label>
              <input id="rn-pay" className="input" value={renewPaymentRef} onChange={(e) => setRenewPaymentRef(e.target.value)} placeholder="es. Fattura 2026/142" maxLength={200} />
            </div>

            {isBulk ? (
              <div className="renew-bulk-list" aria-live="polite">
                {previews.map((p) => (
                  <div className="renew-bulk-row" key={p.l.id}>
                    <span className="rb-dev">{p.l.device_code || 's/n'}</span>
                    <span className="rb-dates">{p.cur ? fmt(p.cur) : '—'}<span className="rb-arrow">→</span><strong>{p.newNotAfter ? fmt(p.newNotAfter) : '—'}</strong></span>
                    {p.wasExpired && <span className="badge badge-danger rb-badge">scaduta</span>}
                  </div>
                ))}
              </div>
            ) : (
              <div className="renew-summary" aria-live="polite">
                <div className="renew-row"><span>Scadenza attuale</span><strong>{single.cur ? fmt(single.cur) : '—'}</strong></div>
                <div className="renew-row"><span>Durata rinnovo</span><strong>{validY ? yearsLabel(y) : '—'}</strong></div>
                <div className="renew-row"><span>Inizio nuova validità</span><strong>{fmt(single.newStart)}</strong></div>
                <div className="renew-row renew-row--hl"><span>Nuova scadenza</span><strong>{single.newNotAfter ? fmt(single.newNotAfter) : '—'}</strong></div>
                {single.wasExpired && <div className="renew-note"><Icon name="alert" size={13} />Licenza già scaduta: i giorni residui non vengono recuperati.</div>}
              </div>
            )}

            <label className="renew-check">
              <span className="sel-check">
                <input type="checkbox" checked={renewConfirmed} onChange={(e) => setRenewConfirmed(e.target.checked)} />
                <span className="sel-check-box"><Icon name="check" size={11} strokeWidth={3} /></span>
              </span>
              <span>Confermo di aver verificato il pagamento{isBulk ? ' di tutte le licenze selezionate' : ' del cliente'}.</span>
            </label>

            {!isBulk && renewHistory.length > 0 && (
              <details className="renew-history">
                <summary>Storico rinnovi ({renewHistory.length})</summary>
                <ul>
                  {renewHistory.map((h) => (
                    <li key={h.id}>{fmt(h.renewed_at)} · +{yearsLabel(h.years)} → {fmt(h.new_not_after)} · {h.renewed_by}{h.payment_reference ? ` · ${h.payment_reference}` : ''}</li>
                  ))}
                </ul>
              </details>
            )}

            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={closeRenew}>Annulla</button>
              <button type="submit" className="btn btn-accent" disabled={renewing || !validY || !renewConfirmed || (isBulk && renewList.length === 0)}>
                <Icon name="refresh" size={14} />{renewing ? 'Rinnovo…' : (isBulk ? `Conferma rinnovo (${renewList.length})` : 'Conferma rinnovo')}
              </button>
            </div>
          </form>
        )}
      </Modal>
    );
  })();

  // Modale "Modifica scadenza": imposta una data esatta invece di sommare
  // anni. Condivisa da Licenze e Scadenziario, come renewModal.
  const editExpiryModal = editExpiryTarget && (
    <Modal title={`Modifica scadenza — ${editExpiryTarget.device_code || 's/n'}`} icon="clock" onClose={closeEditExpiry}>
      <div className="modal-body">
        <div className="renew-row"><span>Scadenza attuale</span><strong>{editExpiryTarget.not_after ? fmt(editExpiryTarget.not_after) : '—'}</strong></div>
        {editExpiryError && <div className="msg msg-error" style={{ margin: '.9rem 0' }}><Icon name="alert" size={16} />{editExpiryError}</div>}
        <div className="field" style={{ marginTop: '.9rem' }}>
          <label htmlFor="ee-date">Nuova scadenza</label>
          <DateTimePicker id="ee-date" value={editExpiryValue} onChange={(e) => setEditExpiryValue(e.target.value)} ariaLabel="Nuova scadenza" />
        </div>
        {editExpiryValue && new Date(editExpiryValue).getTime() <= Date.now() && (
          <div className="renew-note"><Icon name="alert" size={13} />Data nel passato: la licenza risulterà scaduta subito dopo il salvataggio.</div>
        )}
        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={closeEditExpiry}>Annulla</button>
          <button type="button" className="btn btn-accent" disabled={savingEditExpiry || !editExpiryValue} onClick={runSaveEditExpiry}>
            <Icon name="clock" size={14} />{savingEditExpiry ? 'Salvataggio…' : 'Salva scadenza'}
          </button>
        </div>
      </div>
    </Modal>
  );

  // ---- UTENTI ----
  if (section === 'utenti') {
    return <UsersPanel currentUser={user} />;
  }

  // ---- PANORAMICA ----
  if (section === 'panoramica') {
    const stats = [
      { icon: 'key', cls: 'brand', value: codes.filter((c) => c.status === 'unused').length, label: 'Codici disponibili' },
      { icon: 'monitor', cls: 'accent', value: devices.length, label: 'Dispositivi registrati' },
      { icon: 'shield', cls: 'brand', value: licenses.filter((l) => l.status === 'active').length, label: 'Licenze attive' },
      { icon: 'refresh', cls: 'accent', value: requests.filter((r) => r.status === 'pending').length, label: 'Riattivazioni in attesa' },
    ];
    return (
      <>
        <div className="stat-grid">
          {stats.map((s, i) => (
            <div className="stat-card" key={i}>
              <div className={`stat-icon ${s.cls}`}><Icon name={s.icon} size={20} /></div>
              <div className="stat-text">
                <div className="stat-value">{s.value}</div>
                <div className="stat-label">{s.label}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="card">
          <div className="card-head"><h2><Icon name="clock" size={18} />Ultime licenze emesse</h2></div>
          {licenses.length === 0 ? <Empty icon="shield" text="Nessuna licenza ancora emessa" /> : (
            <div className="table-wrap">
              <table className="table">
                <thead><tr><th style={{ width: '18%' }}>Device</th><th style={{ width: '34%' }}>Codice</th><th style={{ width: '16%' }}>Stato</th><th style={{ width: '32%' }}>Emessa</th></tr></thead>
                <tbody>
                  {licenses.slice(0, 6).map((l) => (
                    <tr key={l.id}>
                      <td className="mono strong">{l.device_code || '—'}</td>
                      <td className="mono">{l.activation_code || '—'}</td>
                      <td><LicBadge v={l.status} /></td>
                      <td className="col-date"><Tooltip text={fmt(l.issued_at)}>{fmtShort(l.issued_at)}</Tooltip></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </>
    );
  }

  // ---- CODICI ----
  if (section === 'codici') {
    const openCreateCode = () => { setMessage(null); setCreateCodeOpen(true); };
    return (
      <>
        <div className="card card--fit" ref={codeCardRef}>
          <div className="card-head card-head--stacked">
            <h2><Icon name="key" size={18} />Codici attivazione<HelpTip text="Genera i codici monouso da consegnare al Tecnico per attivare HIPLAAS su una nuova macchina (scegliendo Gruppo ed Etichetta). Revoca un codice se creato per errore o non più utile, finché non è ancora stato usato." /></h2>
            <div className="table-toolbar">
              <SearchInput id="codeSearch" placeholder="Cerca codice, gruppo, etichetta…" value={codeSearch} onChange={(e) => setCodeSearch(e.target.value)} />
              <Select id="codeStatus" className={codeStatus ? 'select-wrap--active' : ''} ariaLabel="Filtra per stato" value={codeStatus} onChange={(e) => setCodeStatus(e.target.value)} options={[
                { value: '', label: 'Tutti gli stati' }, { value: 'unused', label: 'Non usato' }, { value: 'used', label: 'Usato' }, { value: 'revoked', label: 'Revocato' }, { value: 'expired', label: 'Scaduto' },
              ]} />
              <label className="table-toggle" title={codeStatus === 'revoked' ? "Con il filtro su 'Revocato' restano sempre visibili" : undefined}>
                <span>Mostra revocati</span>
                <span className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={showRevokedCode}
                    disabled={codeStatus === 'revoked'}
                    onChange={(e) => setShowRevokedCode(e.target.checked)}
                  />
                  <span className="toggle-switch-track" />
                  <span className="toggle-switch-thumb" />
                </span>
              </label>
              <button type="button" className="btn btn-accent" style={{ marginLeft: 'auto' }} onClick={openCreateCode}><Icon name="plus" size={16} />Nuovo codice</button>
            </div>
          </div>
          {codes.length === 0 ? <Empty icon="key" text="Nessun codice creato" /> : filteredCodes.length === 0 ? <Empty icon="search" text="Nessun risultato per i filtri applicati" /> : (
            <div className="table-wrap" ref={codeWrapRef}>
              <table className="table">
                <thead ref={codeTheadRef}>
                  <tr>
                    <SortableTh label="Codice" sortKey="code" sort={codeColSort} onSort={onCodeSort} style={{ width: '28%' }} />
                    <SortableTh label="Gruppo" sortKey="area_group" sort={codeColSort} onSort={onCodeSort} style={{ width: '10%' }} />
                    <SortableTh label="Etichetta" sortKey="area_label" sort={codeColSort} onSort={onCodeSort} style={{ width: '18%' }} />
                    <SortableTh label="Stato" sortKey="status" sort={codeColSort} onSort={onCodeSort} style={{ width: '12%' }} />
                    <SortableTh label="Creato" sortKey="created_at" sort={codeColSort} onSort={onCodeSort} style={{ width: '16%' }} />
                    <th style={{ width: '16%' }}>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedCodes.map((c) => (
                    <tr key={c.id}>
                      <td className="mono strong">{c.code}</td>
                      <td>{c.area_group}</td><td>{c.area_label}</td>
                      <td><CodeBadge v={c.status} /></td>
                      <td className="col-date"><Tooltip text={fmt(c.created_at)}>{fmtShort(c.created_at)}</Tooltip></td>
                      <td className="actions-cell">
                          {c.status === 'unused' && (
                            <>
                              <button
                                type="button"
                                className="icon-btn"
                                title="Modifica gruppo/etichetta"
                                aria-label={`Modifica area di ${c.code}`}
                                onClick={() => openEditCodeArea(c)}
                              >
                                <Icon name="edit" size={13} />
                              </button>
                              <button className="btn btn-danger btn-sm" disabled={revokingCode} onClick={() => runRevokeCode(c.id)}><Icon name="ban" size={14} />Revoca</button>
                            </>
                          )}
                          {canDeleteRecords && (
                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              title="Elimina definitivamente (solo superadmin/sviluppatore)"
                              disabled={deletingCode}
                              onClick={() => runDeleteCode(c)}
                            >
                              <Icon name="trash" size={14} />Elimina
                            </button>
                          )}
                        </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pagination idPrefix="codePage" total={sortedCodes.length} {...codePagination} onPageChange={codePagination.setPage} onPageSizeChange={codePagination.setPageSize} showSizeSelector={false} />
        </div>
        {createCodeOpen && (
          <Drawer title="Crea codice attivazione" icon="plus" onClose={() => setCreateCodeOpen(false)}>
            <form className="drawer-form" onSubmit={runCreateCode}>
              <div className="field">
                <label htmlFor="ag">Gruppo</label>
                <Combobox id="ag" value={areaGroup} onChange={setAreaGroup} options={groupOptions} placeholder="es. Grecia" required />
              </div>
              <div className="field">
                <label htmlFor="al">Etichetta</label>
                <Combobox id="al" value={areaLabel} onChange={setAreaLabel} options={labelOptions} placeholder="es. Laboratorio 1" required />
              </div>
              <div className="field">
                <label htmlFor="ex">Scadenza (opzionale)</label>
                <DateTimePicker id="ex" value={expiresAt} onChange={(e) => setExpiresAt(e.target.value)} />
              </div>
              <button className="btn btn-accent" type="submit" disabled={creatingCode || !areaGroup.trim() || !areaLabel.trim()}>
                <Icon name="plus" size={16} />{creatingCode ? 'Generazione…' : 'Genera'}
              </button>
            </form>
            {message && <div className={`msg msg-${message.type}`} style={{ marginTop: '1rem' }}><Icon name={message.type === 'success' ? 'check' : 'alert'} size={16} />{message.text}</div>}
          </Drawer>
        )}
        {editCodeAreaTarget && (
          <Modal title={`Modifica area — ${editCodeAreaTarget.code}`} icon="edit" onClose={closeEditCodeArea}>
            <form className="modal-body" onSubmit={submitEditCodeArea}>
              <div className="field">
                <label htmlFor="eca-group">Gruppo</label>
                <Combobox id="eca-group" value={editCodeAreaGroup} onChange={setEditCodeAreaGroup} options={groupOptions} placeholder="es. Grecia" required />
              </div>
              <div className="field">
                <label htmlFor="eca-label">Etichetta</label>
                <Combobox id="eca-label" value={editCodeAreaLabel} onChange={setEditCodeAreaLabel} options={labelOptions} placeholder="es. Laboratorio 1" required />
              </div>
              {editCodeAreaError && <div className="msg msg-error" style={{ marginBottom: '.9rem' }}><Icon name="alert" size={16} />{editCodeAreaError}</div>}
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={closeEditCodeArea}>Annulla</button>
                <button type="submit" className="btn btn-accent" disabled={editCodeAreaSaving || !editCodeAreaGroup.trim() || !editCodeAreaLabel.trim()}>
                  <Icon name="check" size={14} />{editCodeAreaSaving ? 'Salvataggio…' : 'Salva'}
                </button>
              </div>
            </form>
          </Modal>
        )}
      </>
    );
  }

  // ---- DISPOSITIVI ----
  if (section === 'dispositivi') {
    const deviceKpis = [
      { key: 'total', tone: 'brand', icon: 'monitor', value: deviceStats.total, label: 'Totale dispositivi' },
      { key: 'recent', tone: 'success', icon: 'activity', value: deviceStats.recent, label: `Attivi (≤${DEVICE_RECENT_DAYS} gg)` },
      { key: 'silent', tone: 'warn', icon: 'clock', value: deviceStats.silent, label: `Silenti (>${DEVICE_RECENT_DAYS} gg)` },
      { key: 'versions', tone: 'accent', icon: 'package', value: deviceStats.versions, label: 'Versioni in uso' },
    ];
    return (
      <>
        <KpiGrid items={deviceKpis} />
        <div className="card card--fit" ref={deviceCardRef}>
        <div className="card-head card-head--stacked">
          <h2><Icon name="monitor" size={18} />Dispositivi<HelpTip text="Consultala per sapere quali macchine sono attive, dove (Gruppo/Etichetta), se si aggiornano e con che versione. La tabella si aggiorna da sola, senza ricaricare la pagina: quando una macchina contatta il server, la sua riga cambia sotto i tuoi occhi. 'Ultimo contatto' dice da quanto non si fa sentire — non esiste un 'online/offline' perché è sempre la macchina a chiamare noi, mai il contrario." /></h2>
          <div className="table-toolbar">
            <SearchInput id="deviceSearch" placeholder="Cerca device, gruppo, etichetta…" value={deviceSearch} onChange={(e) => setDeviceSearch(e.target.value)} />
          </div>
        </div>
        {devices.length === 0 ? <Empty icon="monitor" text="Nessun dispositivo registrato" /> : filteredDevices.length === 0 ? <Empty icon="search" text="Nessun risultato per i filtri applicati" /> : (
          <div className="table-wrap" ref={deviceWrapRef}>
            <table className="table">
              <thead ref={deviceTheadRef}>
                <tr>
                  <SortableTh label="Device" sortKey="device_code" sort={deviceColSort} onSort={onDeviceSort} style={{ width: '12%' }} />
                  <SortableTh label="Stato" sortKey="license_status" sort={deviceColSort} onSort={onDeviceSort} style={{ width: '10%' }} />
                  <SortableTh label="Gruppo" sortKey="area_group" sort={deviceColSort} onSort={onDeviceSort} style={{ width: '16%' }} />
                  <SortableTh label="Etichetta" sortKey="area_label" sort={deviceColSort} onSort={onDeviceSort} style={{ width: '17%' }} />
                  <SortableTh label="Ultimo contatto" sortKey="last_check_at" sort={deviceColSort} onSort={onDeviceSort} style={{ width: '14%' }} />
                  <SortableTh label="Indirizzo IP" sortKey="ip_address" sort={deviceColSort} onSort={onDeviceSort} style={{ width: '13%' }} />
                  <SortableTh label="Versione" sortKey="last_version" sort={deviceColSort} onSort={onDeviceSort} style={{ width: '11%' }} />
                    <th style={{ width: '7%' }}>Azioni</th>
                </tr>
              </thead>
              <tbody>
                {pagedDevices.map((d) => (
                  <tr key={d.id}>
                    <td className="mono strong">{d.device_code}</td>
                    <td>{d.license_status ? <LicBadge v={d.license_status} /> : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                    <td>{d.area_group}</td>
                      <td>{d.area_label}</td>
                    {/* Tempo trascorso: si legge a colpo d'occhio meglio della data
                        assoluta, che resta comunque nel title (hover). */}
                    <td><Tooltip text={fmt(d.last_check_at)}>{sinceLabel(d.last_check_at)}</Tooltip></td>
                    <td className="mono">{d.ip_address || '—'}</td>
                    <td className="mono">{d.last_version || '—'}</td>
                      <td className="actions-cell">
                        <button
                          type="button"
                          className="icon-btn"
                          title="Modifica gruppo/etichetta"
                          aria-label={`Modifica area di ${d.device_code}`}
                          onClick={() => openEditArea(d)}
                        >
                          <Icon name="edit" size={13} />
                        </button>
                        {canDeleteRecords && (
                          <button
                            type="button"
                            className="icon-btn icon-btn-danger"
                            title="Elimina definitivamente (solo superadmin/sviluppatore)"
                            aria-label={`Elimina ${d.device_code}`}
                            disabled={deletingDevice}
                            onClick={() => runDeleteDevice(d)}
                          >
                            <Icon name="trash" size={13} />
                          </button>
                        )}
                      </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination idPrefix="devicePage" total={sortedDevices.length} {...devicePagination} onPageChange={devicePagination.setPage} onPageSizeChange={devicePagination.setPageSize} showSizeSelector={false} />
        </div>
        {editAreaTarget && (
          <Modal title={`Modifica area — ${editAreaTarget.device_code || 's/n'}`} icon="edit" onClose={closeEditArea}>
            <form className="modal-body" onSubmit={submitEditArea}>
              <div className="field">
                <label htmlFor="ea-group">Gruppo</label>
                <Combobox id="ea-group" value={editAreaGroup} onChange={setEditAreaGroup} options={groupOptions} placeholder="es. Grecia" required />
              </div>
              <div className="field">
                <label htmlFor="ea-label">Etichetta</label>
                <Combobox id="ea-label" value={editAreaLabel} onChange={setEditAreaLabel} options={labelOptions} placeholder="es. Laboratorio 1" required />
              </div>
              {editAreaError && <div className="msg msg-error" style={{ marginBottom: '.9rem' }}><Icon name="alert" size={16} />{editAreaError}</div>}
              <div className="modal-actions">
                <button type="button" className="btn btn-ghost" onClick={closeEditArea}>Annulla</button>
                <button type="submit" className="btn btn-accent" disabled={editAreaSaving || !editAreaGroup.trim() || !editAreaLabel.trim()}>
                  <Icon name="check" size={14} />{editAreaSaving ? 'Salvataggio…' : 'Salva'}
                </button>
              </div>
            </form>
          </Modal>
        )}
      </>
    );
  }

  // ---- LICENZE ----
  if (section === 'licenze') {
    const toggleLic = (v) => setLicStatus((cur) => (cur === v ? '' : v));
    const licKpis = [
      { key: 'total', tone: 'brand', icon: 'shield', value: licStats.total, label: 'Totale licenze' },
      { key: 'inact', tone: 'warn', icon: 'clock', value: licStats.in_activation, label: 'In attivazione' },
      { key: 'active', tone: 'success', icon: 'check', value: licStats.active, label: 'Attive', onClick: () => toggleLic('active'), active: licStatus === 'active' },
      { key: 'suspended', tone: 'warn', icon: 'pause', value: licStats.suspended, label: 'Sospese', onClick: () => toggleLic('suspended'), active: licStatus === 'suspended' },
      { key: 'revoked', tone: 'danger', icon: 'ban', value: licStats.revoked, label: 'Revocate', onClick: () => toggleLic('revoked'), active: licStatus === 'revoked' },
    ];
    const licRenewable = sortedLicenses.filter((l) => RENEWABLE.includes(l.status));
    const licAllSel = licRenewable.length > 0 && licRenewable.every((l) => selectedIds.has(l.id));
    return (
      <>
        <KpiGrid items={licKpis} />
        <div className="card card--fit" ref={licCardRef}>
        <div className="card-head card-head--stacked">
          <h2><Icon name="shield" size={18} />Licenze<HelpTip text="Da qui blocchi una macchina. Sospendi = blocca SOLO gli aggiornamenti, l'app continua a funzionare (reversibile, es. pagamento in ritardo). Revoca = blocca l'ACCESSO all'app: non si apre più. Immediato quando la macchina è online; offline ha effetto entro la scadenza della licenza." /></h2>
          <div className="table-toolbar">
            <SearchInput id="licSearch" placeholder="Cerca device, codice…" value={licSearch} onChange={(e) => setLicSearch(e.target.value)} />
            <Select id="licStatus" className={licStatus ? 'select-wrap--active' : ''} ariaLabel="Filtra per stato" value={licStatus} onChange={(e) => setLicStatus(e.target.value)} options={[
              { value: '', label: 'Tutti gli stati' }, { value: 'waiting', label: 'In setup' }, { value: 'ready_to_activate', label: 'Pronta' }, { value: 'active', label: 'Attiva' }, { value: 'suspended', label: 'Sospesa' }, { value: 'revoked', label: 'Revocata' }, { value: 'cancelled', label: 'Annullata' },
            ]} />
            <label className="table-toggle" title={licStatus === 'revoked' ? "Con il filtro su 'Revocata' restano sempre visibili" : undefined}>
              <span>Mostra revocate</span>
              <span className="toggle-switch">
                <input
                  type="checkbox"
                  checked={showRevokedLic}
                  disabled={licStatus === 'revoked'}
                  onChange={(e) => setShowRevokedLic(e.target.checked)}
                />
                <span className="toggle-switch-track" />
                <span className="toggle-switch-thumb" />
              </span>
            </label>
          </div>
        </div>
        {bulkBar}
        {licenses.length === 0 ? <Empty icon="shield" text="Nessuna licenza" /> : filteredLicenses.length === 0 ? <Empty icon="search" text="Nessun risultato per i filtri applicati" /> : (
            <div className="table-wrap" ref={licWrapRef}>
              <table className="table table--lic">
                <thead ref={licTheadRef}>
                  <tr>
                    <th className="lic-col-check" style={{ width: '3%' }}><label className="sel-check"><input type="checkbox" aria-label="Seleziona tutte le licenze rinnovabili" checked={licAllSel} onChange={() => selectAllRenewable(sortedLicenses)} /><span className="sel-check-box"><Icon name="check" size={11} strokeWidth={3} /></span></label></th>
                    <SortableTh label="Device" className="lic-col-device" sortKey="device_code" sort={licColSort} onSort={onLicSort} style={{ width: '9%' }} />
                    <SortableTh label="Codice" className="lic-col-codice" sortKey="activation_code" sort={licColSort} onSort={onLicSort} style={{ width: '15%' }} />
                    <SortableTh label="Stato" className="lic-col-stato" sortKey="status" sort={licColSort} onSort={onLicSort} style={{ width: '10%' }} />
                    <SortableTh label="Emessa" className="lic-col-emessa" sortKey="issued_at" sort={licColSort} onSort={onLicSort} style={{ width: '11%' }} />
                    <SortableTh label="Ultimo check" className="lic-col-ultimo" sortKey="last_check_at" sort={licColSort} onSort={onLicSort} style={{ width: '11%' }} />
                    <th className="lic-col-azioni" style={{ width: '13%' }}>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedLicenses.map((l) => (
                    <tr key={l.id}>
                      <td>{RENEWABLE.includes(l.status) && <label className="sel-check"><input type="checkbox" aria-label={`Seleziona ${l.device_code || l.id}`} checked={selectedIds.has(l.id)} onChange={() => toggleSelect(l.id)} /><span className="sel-check-box"><Icon name="check" size={11} strokeWidth={3} /></span></label>}</td>
                      <td className="mono strong cell-ellipsis"><Tooltip text={l.device_code}>{l.device_code || '—'}</Tooltip></td>
                      <td className="mono cell-ellipsis"><Tooltip text={l.activation_code}>{l.activation_code || '—'}</Tooltip></td>
                      <td><LicBadge v={l.status} /></td>
                      <td className="col-date"><Tooltip text={fmt(l.issued_at)}>{fmtShort(l.issued_at)}</Tooltip></td>
                      <td className="col-date"><Tooltip text={fmt(l.last_check_at)}>{fmtShort(l.last_check_at)}</Tooltip></td>
                      <td className="actions-cell actions-cell--lic">
                        {LIC_MANAGED.includes(l.status) ? (
                          <RowMenu
                            items={[
                              { label: 'Rinnova', icon: 'refresh', hidden: !RENEWABLE.includes(l.status), onClick: () => openRenew(l) },
                              { label: 'Riattiva', icon: 'play', hidden: l.status === 'active', disabled: settingLicenseStatus, onClick: () => runSetLicenseStatus(l.id, 'active') },
                              { label: 'Riporta in configurazione', icon: 'sliders', tone: 'warn', hidden: l.status !== 'active', disabled: revertingLicense, onClick: () => runRevertLicense(l.id) },
                              { label: 'Blocca aggiornamenti', icon: 'pause', tone: 'warn', hidden: l.status === 'suspended', disabled: settingLicenseStatus, onClick: () => runSetLicenseStatus(l.id, 'suspended') },
                              { label: 'Blocca accesso', icon: 'ban', tone: 'danger', hidden: l.status === 'revoked', disabled: settingLicenseStatus, onClick: () => runSetLicenseStatus(l.id, 'revoked') },
                            ]}
                          />
                        ) : (
                          <span className="actions-cell-note">
                            {l.status === 'waiting' ? 'Configurazione dal tecnico in corso'
                              : l.status === 'ready_to_activate' ? 'In attesa del riavvio di attivazione'
                              : 'Setup annullato'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
        )}
        <Pagination idPrefix="licPage" total={sortedLicenses.length} {...licPagination} onPageChange={licPagination.setPage} onPageSizeChange={licPagination.setPageSize} showSizeSelector={false} />
        </div>
        {renewModal}
      </>
    );
  }

  // ---- SCADENZIARIO ----
  if (section === 'scadenziario') {
    const nextLabel = scadStats.nextDays == null ? '—' : scadStats.nextDays === 0 ? 'Oggi' : `${scadStats.nextDays} gg`;
    // Le 3 KPI di conteggio fanno anche da filtro rapido (toggle) sulla tabella.
    const toggleScad = (f) => setScadFilter((cur) => (cur === f ? '' : f));
    const scadKpis = [
      { key: 'expired', tone: 'danger', icon: 'alert', value: scadStats.expired, label: 'Scadute', onClick: () => toggleScad('expired'), active: scadFilter === 'expired' },
      { key: 'soon', tone: 'warn', icon: 'clock', value: scadStats.soon, label: 'In scadenza (≤30 gg)', onClick: () => toggleScad('soon'), active: scadFilter === 'soon' },
      { key: 'valid', tone: 'success', icon: 'shield', value: scadStats.valid, label: 'Valide (>30 gg)', onClick: () => toggleScad('valid'), active: scadFilter === 'valid' },
      { key: 'next', tone: 'brand', icon: 'activity', value: nextLabel, label: 'Prossima scadenza' },
    ];
    const scadRenewable = sortedScad.filter((l) => RENEWABLE.includes(l.status));
    const scadAllSel = scadRenewable.length > 0 && scadRenewable.every((l) => selectedIds.has(l.id));
    return (
      <>
        <KpiGrid items={scadKpis} />
        <div className="card card--fit" ref={scadCardRef}>
          <div className="card-head card-head--stacked">
            <h2><Icon name="clock" size={18} />Scadenziario<HelpTip text="Elenco delle licenze ordinate per scadenza. La scadenza e' la data entro cui la macchina deve riconnettersi per rinnovare la licenza firmata: oltre quella data la licenza offline smette di essere valida. Le KPI in alto fanno anche da filtro rapido: cliccale per isolare scadute, in scadenza o valide." /></h2>
            <div className="table-toolbar">
              <SearchInput id="scadSearch" placeholder="Cerca device, codice…" value={scadSearch} onChange={(e) => setScadSearch(e.target.value)} />
              <Select id="scadFilter" className={scadFilter ? 'select-wrap--active' : ''} ariaLabel="Filtra per scadenza" value={scadFilter} onChange={(e) => setScadFilter(e.target.value)} options={[
                { value: '', label: 'Tutte le scadenze' }, { value: 'expired', label: 'Scadute' }, { value: 'soon', label: 'In scadenza (≤30 gg)' }, { value: 'valid', label: 'Valide (>30 gg)' },
              ]} />
            </div>
          </div>
          {bulkBar}
          {expiringBase.length === 0 ? <Empty icon="clock" text="Nessuna licenza con scadenza" /> : filteredScad.length === 0 ? <Empty icon="search" text="Nessun risultato per i filtri applicati" /> : (
              <div className="table-wrap" ref={scadWrapRef}>
                <table className="table">
                  <thead ref={scadTheadRef}>
                    <tr>
                      <th className="scad-col-check" style={{ width: '4%' }}><label className="sel-check"><input type="checkbox" aria-label="Seleziona tutte le licenze rinnovabili" checked={scadAllSel} onChange={() => selectAllRenewable(sortedScad)} /><span className="sel-check-box"><Icon name="check" size={11} strokeWidth={3} /></span></label></th>
                      <SortableTh label="Device" className="scad-col-device" sortKey="device_code" sort={scadColSort} onSort={onScadSort} style={{ width: '12%' }} />
                      <SortableTh label="Codice" className="scad-col-codice" sortKey="activation_code" sort={scadColSort} onSort={onScadSort} style={{ width: '18%' }} />
                      <SortableTh label="Stato" className="scad-col-stato" sortKey="status" sort={scadColSort} onSort={onScadSort} style={{ width: '10%' }} />
                      <SortableTh label="Scadenza" className="scad-col-scadenza" sortKey="not_after" sort={scadColSort} onSort={onScadSort} style={{ width: '16%' }} />
                      <th className="scad-col-scadetra" style={{ width: '16%' }}>Scade tra</th>
                      <th className="scad-col-azioni" style={{ width: '24%' }}>Azioni</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedScad.map((l) => {
                      const d = daysUntil(l.not_after);
                      const expired = d < 0;
                      const soon = !expired && d <= EXPIRY_SOON_DAYS;
                      return (
                        <tr key={l.id}>
                          <td>{RENEWABLE.includes(l.status) && <label className="sel-check"><input type="checkbox" aria-label={`Seleziona ${l.device_code || l.id}`} checked={selectedIds.has(l.id)} onChange={() => toggleSelect(l.id)} /><span className="sel-check-box"><Icon name="check" size={11} strokeWidth={3} /></span></label>}</td>
                          <td className="mono strong cell-ellipsis"><Tooltip text={l.device_code}>{l.device_code || '—'}</Tooltip></td>
                          <td className="mono cell-ellipsis"><Tooltip text={l.activation_code}>{l.activation_code || '—'}</Tooltip></td>
                          <td><LicBadge v={l.status} /></td>
                          <td className="col-date"><Tooltip text={fmt(l.not_after)}>{fmtShort(l.not_after)}</Tooltip></td>
                          <td>
                            {expired
                              ? <span className="badge badge-danger">Scaduta da {Math.abs(d)} gg</span>
                              : <span className={`badge ${soon ? 'badge-warn' : 'badge-success'}`}>{d === 0 ? 'Scade oggi' : `Tra ${d} gg`}</span>}
                          </td>
                          <td className="actions-cell">
                            {RENEWABLE.includes(l.status) && <button className="btn btn-accent btn-sm" onClick={() => openRenew(l)} title="Rinnova la licenza dopo aver verificato il pagamento"><Icon name="refresh" size={14} />Rinnova</button>}
                            {RENEWABLE.includes(l.status) && <button className="btn btn-ghost btn-sm" onClick={() => openEditExpiry(l)} title="Imposta una data di scadenza esatta"><Icon name="edit" size={14} />Modifica</button>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
          )}
          <Pagination idPrefix="scadPage" total={sortedScad.length} {...scadPagination} onPageChange={scadPagination.setPage} onPageSizeChange={scadPagination.setPageSize} showSizeSelector={false} />
        </div>
        {renewModal}
        {editExpiryModal}
      </>
    );
  }

  // ---- RIATTIVAZIONI ----
  if (section === 'riattivazioni') {
    const toggleReq = (v) => setReqStatus((cur) => (cur === v ? '' : v));
    const reqKpis = [
      { key: 'pending', tone: 'warn', icon: 'clock', value: reqStats.pending, label: 'In attesa', onClick: () => toggleReq('pending'), active: reqStatus === 'pending' },
      { key: 'approved', tone: 'success', icon: 'check', value: reqStats.approved, label: 'Approvate', onClick: () => toggleReq('approved'), active: reqStatus === 'approved' },
      { key: 'rejected', tone: 'danger', icon: 'ban', value: reqStats.rejected, label: 'Rifiutate', onClick: () => toggleReq('rejected'), active: reqStatus === 'rejected' },
      { key: 'total', tone: 'brand', icon: 'refresh', value: reqStats.total, label: 'Totale richieste' },
    ];
    return (
      <>
        <KpiGrid items={reqKpis} />
        <div className="card card--fit" ref={reqCardRef}>
        <div className="card-head card-head--stacked">
          <h2><Icon name="refresh" size={18} />Richieste di riattivazione<HelpTip text="Qui arrivano le richieste delle macchine che non si riconoscono più (cambio di due o più componenti, o PC sostituito). Approva quando il cambio è legittimo — la licenza viene rilegata al nuovo hardware — oppure rifiuta se la richiesta è sospetta." /></h2>
          <div className="table-toolbar">
            <SearchInput id="reqSearch" placeholder="Cerca device, motivo…" value={reqSearch} onChange={(e) => setReqSearch(e.target.value)} />
            <Select id="reqStatus" className={reqStatus ? 'select-wrap--active' : ''} ariaLabel="Filtra per stato" value={reqStatus} onChange={(e) => setReqStatus(e.target.value)} options={[
              { value: '', label: 'Tutti gli stati' }, { value: 'pending', label: 'In attesa' }, { value: 'approved', label: 'Approvate' }, { value: 'rejected', label: 'Rifiutate' },
            ]} />
          </div>
        </div>
        {requests.length === 0 ? <Empty icon="refresh" text="Nessuna richiesta" /> : filteredRequests.length === 0 ? <Empty icon="search" text="Nessun risultato per i filtri applicati" /> : (
            <div className="table-wrap" ref={reqWrapRef}>
              <table className="table">
                <thead ref={reqTheadRef}>
                  <tr>
                    <SortableTh label="Device" sortKey="device_code" sort={reqColSort} onSort={onReqSort} style={{ width: '12%' }} />
                    <SortableTh label="Match" sortKey="matched_components" sort={reqColSort} onSort={onReqSort} style={{ width: '8%' }} />
                    <SortableTh label="Motivo" sortKey="reason" sort={reqColSort} onSort={onReqSort} style={{ width: '30%' }} />
                    <SortableTh label="Stato" sortKey="status" sort={reqColSort} onSort={onReqSort} style={{ width: '10%' }} />
                    <SortableTh label="Richiesta" sortKey="requested_at" sort={reqColSort} onSort={onReqSort} style={{ width: '15%' }} />
                    <th style={{ width: '25%' }}>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedRequests.map((r) => (
                    <tr key={r.id}>
                      <td className="mono strong">{r.device_code || '—'}</td>
                      <td>{r.matched_components == null ? '—' : `${r.matched_components}/3`}</td>
                      <td>{r.reason || '—'}</td>
                      <td><Badge v={r.status} /></td>
                      <td className="col-date"><Tooltip text={fmt(r.requested_at)}>{fmtShort(r.requested_at)}</Tooltip></td>
                      <td className="actions-cell">
                        {r.status === 'pending' && <>
                          <button className="btn btn-primary btn-sm" disabled={resolvingRequest} onClick={() => runResolveRequest(r.id, 'approved')}><Icon name="check" size={14} />Approva</button>
                          <button className="btn btn-danger btn-sm" disabled={resolvingRequest} onClick={() => runResolveRequest(r.id, 'rejected')}><Icon name="x" size={14} />Rifiuta</button>
                        </>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
        )}
        <Pagination idPrefix="reqPage" total={sortedRequests.length} {...reqPagination} onPageChange={reqPagination.setPage} onPageSizeChange={reqPagination.setPageSize} showSizeSelector={false} />
        </div>
      </>
    );
  }

  // ---- SBLOCCO PASSWORD ADMIN ----
  if (section === 'admin-password-reset') {
    const togglePw = (v) => setPwStatus((cur) => (cur === v ? '' : v));
    const pwKpis = [
      { key: 'pending', tone: 'warn', icon: 'clock', value: pwResetStats.pending, label: 'In attesa di codice', onClick: () => togglePw('pending'), active: pwStatus === 'pending' },
      { key: 'approved', tone: 'accent', icon: 'key', value: pwResetStats.approved, label: 'Codice generato', onClick: () => togglePw('approved'), active: pwStatus === 'approved' },
      { key: 'consumed', tone: 'success', icon: 'check', value: pwResetStats.consumed, label: 'Sbloccate', onClick: () => togglePw('consumed'), active: pwStatus === 'consumed' },
      { key: 'total', tone: 'brand', icon: 'key', value: pwResetStats.total, label: 'Totale richieste' },
    ];
    return (
      <>
        <KpiGrid items={pwKpis} />
        <div className="card card--fit" ref={pwCardRef}>
        <div className="card-head card-head--stacked">
          <h2><Icon name="key" size={18} />Sblocco password admin<HelpTip text="Qui arrivano le richieste delle macchine il cui operatore ha dimenticato la password dell'account admin locale. Genera un codice monouso (valido 30 minuti) e leggilo al telefono a chi lo ha richiesto: il manager non conosce mai la password reale, solo questo codice di sblocco." /></h2>
          <div className="table-toolbar">
            <SearchInput id="pwSearch" placeholder="Cerca device, utente…" value={pwSearch} onChange={(e) => setPwSearch(e.target.value)} />
            <Select id="pwStatus" className={pwStatus ? 'select-wrap--active' : ''} ariaLabel="Filtra per stato" value={pwStatus} onChange={(e) => setPwStatus(e.target.value)} options={[
              { value: '', label: 'Tutti gli stati' },
              { value: 'pending', label: 'In attesa' },
              { value: 'approved', label: 'Codice generato' },
              { value: 'consumed', label: 'Consumato' },
              { value: 'expired', label: 'Scaduto' },
              { value: 'rejected', label: 'Invalidata' },
            ]} />
          </div>
        </div>
        {pwNotice && <div className={`msg msg-${pwNotice.type}`} style={{ margin: '0 0 .9rem' }}><Icon name={pwNotice.type === 'success' ? 'check' : 'alert'} size={16} />{pwNotice.text}</div>}
        {pwResets.length === 0 ? <Empty icon="key" text="Nessuna richiesta di sblocco" /> : filteredPwResets.length === 0 ? <Empty icon="search" text="Nessun risultato per i filtri applicati" /> : (
            <div className="table-wrap" ref={pwWrapRef}>
              <table className="table">
                <thead ref={pwTheadRef}>
                  <tr>
                    <SortableTh label="Device" sortKey="device_code" sort={pwColSort} onSort={onPwSort} style={{ width: '15%' }} />
                    <SortableTh label="Utente" sortKey="username" sort={pwColSort} onSort={onPwSort} style={{ width: '13%' }} />
                    <SortableTh label="Stato" sortKey="status" sort={pwColSort} onSort={onPwSort} style={{ width: '14%' }} />
                    <SortableTh label="Richiesta" sortKey="requested_at" sort={pwColSort} onSort={onPwSort} style={{ width: '17%' }} />
                    <SortableTh label="Scadenza codice" sortKey="code_expires_at" sort={pwColSort} onSort={onPwSort} style={{ width: '17%' }} />
                    <th style={{ width: '24%' }}>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedPwResets.map((r) => (
                    <tr key={r.id}>
                      <td className="mono strong">{r.device_code || '—'}</td>
                      <td className="mono">{r.username || '—'}</td>
                      <td><PwResetBadge v={r.status} /></td>
                      <td className="col-date"><Tooltip text={fmt(r.requested_at)}>{fmtShort(r.requested_at)}</Tooltip></td>
                      <td className="col-date">{r.code_expires_at ? <Tooltip text={fmt(r.code_expires_at)}>{fmtShort(r.code_expires_at)}</Tooltip> : '—'}</td>
                      <td className="actions-cell">
                        {r.status === 'pending' && (
                          <button className="btn btn-primary btn-sm" disabled={generatingPwCode} onClick={() => runGeneratePwCode(r)}>
                            <Icon name="key" size={14} />{generatingPwCode ? 'Generazione…' : 'Genera codice di reset'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
        )}
        <Pagination idPrefix="pwPage" total={sortedPwResets.length} {...pwResetPagination} onPageChange={pwResetPagination.setPage} onPageSizeChange={pwResetPagination.setPageSize} showSizeSelector={false} />
        </div>
        {pwCodeModal && (
          <Modal title={`Codice di sblocco — ${pwCodeModal.request.device_code || pwCodeModal.request.username || 's/n'}`} icon="key" onClose={closePwCodeModal}>
            <div className="modal-body">
              <div className="msg msg-warn" style={{ marginBottom: '1rem' }}>
                <Icon name="alert" size={16} />
                Leggi questo codice al telefono all'operatore adesso: non sarà più visibile dopo aver chiuso questa finestra, in nessuna schermata del pannello.
              </div>
              <PwCodeBox code={pwCodeModal.code} />
              <p className="pw-code-hint">Valido una sola volta, scade il {fmt(pwCodeModal.expiresAt)}.</p>
              <div className="modal-actions">
                <button type="button" className="btn btn-primary" onClick={closePwCodeModal}>Ho letto il codice, chiudi</button>
              </div>
            </div>
          </Modal>
        )}
      </>
    );
  }

  return null;
}

export default DashboardAmministratore;
